import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import { PayButton } from "@/components/PayButton";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import Logo from "@/components/Logo";

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return s; }
}

export default function CustomerInvoicePortal() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/invoices/${token}`);
        setInv(data);
      } catch (e) { setErr(e?.response?.data?.detail || "Invoice not found"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen bg-[#050505] text-white/40 flex items-center justify-center text-sm">Loading…</div>;
  if (err) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6"><div className="text-center"><div className="text-[10px] uppercase tracking-[0.3em] text-red-300 mb-2">Error</div><div className="text-sm text-white/70">{err}</div></div></div>;
  if (!inv) return null;

  return (
    <div className="min-h-screen bg-[#f6f4ee] text-[#0a0a0a]">
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="bg-white border border-gray-200 shadow-sm" data-testid="customer-invoice-page">
          <div className="bg-[#050505] text-white px-6 sm:px-10 py-8 flex items-center justify-between">
            <Logo size="sm" />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F]">Invoice</div>
              <div className="text-lg font-mono" data-testid="invoice-number">{inv.invoice_number}</div>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">Bill To</div>
                <div className="font-medium">{inv.client_name}</div>
                {inv.client_email && <div className="text-gray-600">{inv.client_email}</div>}
                {inv.client_phone && <div className="text-gray-600">{inv.client_phone}</div>}
              </div>
              <div className="sm:text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">Details</div>
                <div className="text-gray-700">Issued {fmtDate(inv.created_at)}</div>
                {inv.event_date && <div className="text-gray-700">Service {fmtDate(inv.event_date)}</div>}
                {inv.event_location && <div className="text-gray-600">{inv.event_location}</div>}
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border border-[#C9963F] text-[#9a7228]">{inv.status}</span>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9963F] border-b border-gray-200 pb-2 mb-3">Items</div>
              {(inv.line_items || []).length === 0 ? (
                <div className="text-sm text-gray-500 italic py-4">No line items</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-200">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2 w-16">Qty</th>
                      <th className="text-right py-2 w-24">Unit</th>
                      <th className="text-right py-2 w-24">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inv.line_items || []).map((it) => {
                      const qty = Number(it.quantity) || 1;
                      const up = Number(it.unit_price) || 0;
                      return (
                        <tr key={it.id} className="border-b border-gray-100">
                          <td className="py-2">{it.label || "—"}</td>
                          <td className="py-2 text-right">{qty}</td>
                          <td className="py-2 text-right">{money(up)}</td>
                          <td className="py-2 text-right">{money(qty * up)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                <Row label="Subtotal" value={money(inv.subtotal)} />
                <Row label="Tax" value={money(inv.tax)} />
                <div className="border-t border-gray-300 mt-2 pt-2 w-64 flex justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-gray-600 mt-1">Total</span>
                  <span className="font-serif-brand text-2xl text-[#0a0a0a]" data-testid="invoice-total">{money(inv.total)}</span>
                </div>
              </div>
            </div>

            {/* Payment Schedule */}
            {(inv.installments || []).length > 0 && (
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9963F] border-b border-gray-200 pb-2 mb-3">Payment Schedule</div>
                <div className="space-y-2">
                  {inv.installments.map((p, idx) => {
                    const paid = !!p.paid;
                    const link = (p.payment_link || "").trim();
                    return (
                      <div key={p.id || idx} className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0" data-testid={`inv-installment-${idx}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{p.label || `Payment ${idx + 1}`}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {paid ? (
                              <span className="text-[#9a7228]">✓ PAID{p.method ? ` · ${p.method}` : ""}{p.paid_at ? ` · ${fmtDate(p.paid_at)}` : ""}</span>
                            ) : (
                              <span>{p.due_date ? `Due ${fmtDate(p.due_date)}` : "Due upon receipt"}</span>
                            )}
                          </div>
                          {!paid && link && (
                            <PayButton link={link} label={p.label || `Payment ${idx + 1}`} testid={`inv-pay-link-${idx}`} />
                          )}
                        </div>
                        <div className={`font-serif-brand text-xl shrink-0 ${paid ? "text-[#9a7228] line-through decoration-1" : "text-[#0a0a0a]"}`}>{money(p.amount)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-baseline justify-between border-t border-gray-200 pt-3">
                  <span className="text-[10px] uppercase tracking-widest text-gray-600">Outstanding</span>
                  <span className={`font-serif-brand text-2xl ${Number(inv.amount_outstanding) === 0 ? "text-[#9a7228]" : "text-[#0a0a0a]"}`} data-testid="inv-outstanding-public">{money(inv.amount_outstanding)}</span>
                </div>
              </div>
            )}

            {inv.memo && (
              <div className="mt-8 border-t border-gray-200 pt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">Note</div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{inv.memo}</div>
              </div>
            )}

            {inv.menu_description && (
              <div className="mt-8 border-t border-gray-200 pt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9a7228] mb-2">Menu</div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{inv.menu_description}</div>
                {Number(inv.guest_count) > 0 && (
                  <div className="text-xs text-gray-500 mt-2">Guest count: {inv.guest_count}</div>
                )}
              </div>
            )}

            <div className="mt-8 flex justify-end">
              <a href={`${API}/public/invoices/${token}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="inv-download-pdf"
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 text-sm hover:border-[#C9963F] hover:text-[#9a7228]">
                <Download size={14} /> Download PDF
              </a>
            </div>
          </div>
        </div>

        <footer className="text-center mt-8 text-[10px] uppercase tracking-[0.3em] text-gray-400">
          Sheesh Eatery & Lounge · 4707 State Hwy 121 · The Colony, TX
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="w-64 flex items-baseline justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
}
